import type { SupabaseClient } from '@supabase/supabase-js';
import { rememberListerChannel } from '../../lib/lister-route.js';
import { KF_ICON, KF_LOGO_GOECOMMERCE, showToast } from '@vario-connect/frontend-design';
import { awaitJob } from '../../lib/await-job.js';
import { renderRequiredAttrsPill } from '../shared/required-attrs.js';
import {
  renderListingDetailPane,
  STATUS_PILL,
  type ListingStatus,
} from '../shared/listing-detail-pane.js';

// ============================================================================
// Otto Lister — Inbox-Master (storefront-frei).
//
//   * Die Inbox otto.listings wird ausschliesslich vom Vario-Slot befuellt
//     (otto_upsert_listing_from_slot) — kein Hintergrund-Scan.
//   * Diese Maske ist NUR der Master: Inbox-Liste, Status-Filter, Suche, Pager.
//     Das Detail rechts rendert das geteilte views/shared/listing-detail-pane.ts.
//   * Otto ist einmarktig (storefront fix 'otto') → kein Storefront-/Modus-Filter.
//   * Status-Lifecycle (dieser Spec): pending → mapping → mapped (Push folgt).
// ============================================================================

interface OttoListCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
}

interface OttoListSlotCtx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind?: string };
  supabaseUrl: string;
}

/** Standalone-Einstieg (Dashboard-Tab / Sales-Channels-Slot → /#/list). */
export function renderOttoList(ctx: OttoListSlotCtx): void {
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = '';
  void renderOttoListTab(
    ctx.contentEl,
    { supabase: ctx.supabase, session: ctx.session, supabaseUrl: ctx.supabaseUrl },
    { standalone: true },
  );
}

const STATUS_CLS: Record<ListingStatus, string> = {
  pending: 'st-pending',
  mapping: 'st-mapping',
  mapped: 'st-mapped',
  pushing: 'st-mapping',
  pushed: 'st-mapped',
  error: 'st-error',
  archived: 'st-archived',
};

const TAG_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><path d="M7 7h.01"/></svg>';
const WARN_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/></svg>';
const PG_FIRST =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m11 17-5-5 5-5M18 17l-5-5 5-5"/></svg>';
const PG_PREV =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m15 18-6-6 6-6"/></svg>';
const PG_NEXT =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 18 6-6-6-6"/></svg>';
const PG_LAST =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m13 17 5-5-5-5M6 17l5-5-5-5"/></svg>';

interface ListingRow {
  id: string;
  vario_article_id: string;
  vario_listing_id: string;
  vario_article_number: string | null;
  vario_article_name: string | null;
  otto_category: string | null;
  otto_brand_id: string | null;
  status: ListingStatus;
  is_variant_parent: boolean;
  /** Über eine Bestellung zugeordnet (Snapshot source='order-match', RPC 0302) → noch nicht auf
   *  Otto gelistet; im Lister als „aus Bestellung" markiert. */
  from_order: boolean;
  last_error: string | null;
  first_seen_at: string;
  updated_at: string;
  /** Pflichtattribut-Prüfung der letzten Vorschau (Migration 0016); null = noch nie geprüft. */
  required_attrs_total: number | null;
  required_attrs_missing: string[] | null;
  total_count: number;
}

const STATUS_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '',            label: 'Alle (offen)' },
  // „In Bearbeitung" = Status-GRUPPE pending|mapping|mapped (alles Vor-Live); Auflösung in der
  // RPC otto_list_listings (Migration 0294). „Aktiv" = pushed (auf Otto gelistet).
  { value: 'in_progress', label: 'In Bearbeitung' },
  { value: 'pushed',      label: 'Aktiv' },
  { value: 'error',       label: 'Fehler' },
];

interface FilterState {
  status: string;
  search: string;
}

const PAGE_SIZE = 50;

export async function renderOttoListTab(
  host: HTMLElement,
  ctx: OttoListCtx,
  opts: { standalone?: boolean } = {},
) {
  const standalone = !!opts.standalone;
  host.innerHTML = renderShell(standalone);

  const root = host.querySelector<HTMLElement>('.kf-page')!;
  if (standalone) {
    const fit = () => { root.style.height = window.innerHeight + 'px'; };
    window.addEventListener('resize', fit);
    window.addEventListener('orientationchange', fit);
    fit();
  }

  const state: FilterState = { status: '', search: '' };
  let activePage = 0;
  let activeListingId: string | null = null;
  let lastRows: ListingRow[] = [];

  const statusSel = host.querySelector<HTMLSelectElement>('#klst-status')!;
  const pgFirst = host.querySelector<HTMLButtonElement>('#klst-pg-first')!;
  const pgPrev = host.querySelector<HTMLButtonElement>('#klst-pg-prev')!;
  const pgNext = host.querySelector<HTMLButtonElement>('#klst-pg-next')!;
  const pgLast = host.querySelector<HTMLButtonElement>('#klst-pg-last')!;

  statusSel.innerHTML = STATUS_FILTER_OPTIONS.map((o) => `<option value="${o.value}">${o.label}</option>`).join('');

  statusSel.addEventListener('change', () => {
    state.status = statusSel.value;
    activePage = 0;
    activeListingId = null;
    void reloadList();
  });

  const searchInput = host.querySelector<HTMLInputElement>('#klst-search')!;
  const searchClear = host.querySelector<HTMLButtonElement>('#klst-search-clear')!;
  const updateClearVis = () => { searchClear.classList.toggle('show', searchInput.value.length > 0); };
  searchInput.addEventListener('input', () => {
    state.search = searchInput.value;
    updateClearVis();
    applyClientSearchFilter(host, state.search);
  });
  searchClear.addEventListener('click', () => {
    searchInput.value = '';
    state.search = '';
    updateClearVis();
    applyClientSearchFilter(host, '');
    searchInput.focus();
  });
  pgFirst.addEventListener('click', () => { activePage = 0; void reloadList(); });
  pgPrev.addEventListener('click', () => { if (activePage > 0) { activePage -= 1; void reloadList(); } });
  pgNext.addEventListener('click', () => { activePage += 1; void reloadList(); });
  pgLast.addEventListener('click', () => {
    const total = lastRows[0]?.total_count ?? 0;
    activePage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
    void reloadList();
  });

  // Slot-Titelleiste (nur im Vario-Slot vorhanden): „Speichern" spiegelt den Speichern-Handler des
  // aktiven Detail-Tabs (via onSaveHandler); „Senden" pusht das aktuell gewählte Listing an Otto
  // (otto_enqueue_push — wie im Artikel-Slot). Im Standalone existieren die Buttons nicht (null).
  let currentSave: (() => Promise<void>) | null = null;
  const slotSaveBtn = host.querySelector<HTMLButtonElement>('#slot-save');
  const slotSendBtn = host.querySelector<HTMLButtonElement>('#slot-send');
  slotSaveBtn?.addEventListener('click', () => { void currentSave?.(); });
  slotSendBtn?.addEventListener('click', () => { if (activeListingId) void sendToOtto(activeListingId); });

  // Kanal-Parameter dieses Reiters merken: daraus baut der Artikel-Slot den Sprung hierher.
  rememberListerChannel(window.location.search);

  await reloadList();

  async function sendToOtto(id: string): Promise<void> {
    if (slotSendBtn) slotSendBtn.disabled = true;
    const { data: jobId, error } = await ctx.supabase.rpc('otto_enqueue_push', { _id: id });
    if (error) {
      showToast(`Senden fehlgeschlagen: ${error.message}`, 'error');
      if (slotSendBtn) slotSendBtn.disabled = false;
      return;
    }
    showToast('An OTTO gesendet — Übertragung läuft…', 'success');
    await reloadList(); // zeigt sofort „Wird übertragen"
    if (typeof jobId !== 'string') { if (slotSendBtn) slotSendBtn.disabled = false; return; }
    try {
      const job = await awaitJob(ctx.supabase, jobId, { timeoutMs: 120_000, pollIntervalMs: 2_000 });
      if (activeListingId !== id) return; // Nutzer hat inzwischen ein anderes Listing gewählt
      await reloadList();
      if (job.status === 'failed') showToast('Übertragung fehlgeschlagen — Details im Vorschau-Tab.', 'error');
      else showToast('Übertragung abgeschlossen — Status aktualisiert.', 'info');
    } catch {
      if (activeListingId === id) await reloadList();
      showToast('Übertragung dauert länger als erwartet — Status ggf. per Neuladen prüfen.', 'info');
    } finally {
      if (slotSendBtn && activeListingId === id) slotSendBtn.disabled = false;
    }
  }

  function showDetail(id: string) {
    const detailHost = host.querySelector<HTMLDivElement>('#klst-detail')!;
    // „Senden" für das gewählte Listing freigeben (Push via otto_enqueue_push).
    if (slotSendBtn) { slotSendBtn.hidden = false; slotSendBtn.disabled = false; }
    void renderListingDetailPane(detailHost, ctx, id, {
      kind: 'lister',
      // Die Lister-Titelleiste führt den Speichern-Button (externalSave) → der Pane rendert keinen
      // eigenen; in allen Pfaden gleich (Titelleiste ist immer da).
      externalSave: true,
      onListingChanged: () => void reloadList(),
      // Nach per-Tab-Speichern nur die linke Liste (Status) neu laden — das Detail-Pane bleibt
      // stehen (aktiver Tab + Eingaben erhalten), anders als onListingChanged bei Kategorie-Wechsel.
      onSaved: () => void reloadList({ keepDetail: true }),
      // Chip „X/Y Pflichtattr.“ erst nach der (mehrere Sekunden langen) Prüfung aktuell.
      onRequiredAttrsChecked: () => void reloadList({ keepDetail: true }),
      onDeleted: () => {
        activeListingId = null;
        if (slotSaveBtn) slotSaveBtn.hidden = true;
        if (slotSendBtn) slotSendBtn.hidden = true;
        clearDetail('Wähle ein Listing aus der Liste links.');
        void reloadList();
      },
      // Slot-Titelleiste: Speichern-Handler des aktiven Tabs spiegeln (Label + Sichtbarkeit).
      // Im Standalone existiert #slot-save nicht (Button=null) → Guards machen es zum No-Op.
      onSaveHandler: (info) => {
        currentSave = info.save;
        if (slotSaveBtn) {
          slotSaveBtn.hidden = !info.visible;
          slotSaveBtn.innerHTML = `<span class="v-icon fa-regular fa-floppy-disk"></span> ${info.label}`;
        }
      },
    });
  }

  async function reloadList(opts: { keepDetail?: boolean } = {}) {
    const listHost = host.querySelector<HTMLDivElement>('#klst-list')!;
    listHost.innerHTML = `<div class="li-loading">Lade…</div>`;
    const { data, error } = await ctx.supabase.rpc('otto_list_listings', {
      _status: state.status || null,
      _limit: PAGE_SIZE,
      _offset: activePage * PAGE_SIZE,
    });
    if (error) {
      listHost.innerHTML = `<div class="li-loading err">Fehler: ${escapeHtml(error.message)}</div>`;
      updatePager(0);
      return;
    }
    lastRows = (data ?? []) as ListingRow[];
    const total = lastRows[0]?.total_count ?? 0;
    renderMasterList(listHost, lastRows, activeListingId);
    updatePager(total);

    listHost.querySelectorAll<HTMLElement>('[data-listing-id]').forEach((el) => {
      el.addEventListener('click', () => {
        const id = el.dataset['listingId'];
        if (!id) return;
        activeListingId = id;
        listHost.querySelectorAll('.li').forEach((r) => r.classList.remove('sel'));
        el.classList.add('sel');
        showDetail(id);
      });
    });

    if (state.search) applyClientSearchFilter(host, state.search);

    // Nach per-Tab-Speichern (onSaved) NUR die linke Liste/Status auffrischen — das Detail-Pane
    // NICHT via showDetail neu aufbauen (sonst Sprung auf den Default-Tab „Übersicht" + Verlust der
    // Eingaben). Die Selektion links bleibt über renderMasterList(activeListingId) markiert.
    if (opts.keepDetail) return;

    const focusListingId = consumeFocusListingId();
    if (focusListingId) {
      const hit = lastRows.find((r) => r.vario_listing_id === focusListingId);
      if (hit) {
        activeListingId = hit.id;
        listHost.querySelector<HTMLElement>(`[data-listing-id="${hit.id}"]`)?.classList.add('sel');
        showDetail(hit.id);
        return;
      }
      void resolveListingByVarioListingId(focusListingId).then((resolved) => {
        if (resolved) {
          activeListingId = resolved;
          showDetail(resolved);
        } else if (lastRows[0]) {
          activeListingId = lastRows[0].id;
          listHost.querySelector<HTMLElement>(`[data-listing-id="${lastRows[0].id}"]`)?.classList.add('sel');
          showDetail(lastRows[0].id);
        }
      });
      return;
    }

    if (!activeListingId && lastRows[0]) {
      activeListingId = lastRows[0].id;
      listHost.querySelector<HTMLElement>(`[data-listing-id="${lastRows[0].id}"]`)?.classList.add('sel');
      showDetail(lastRows[0].id);
    } else if (activeListingId) {
      showDetail(activeListingId);
    } else {
      clearDetail('Wähle ein Listing aus der Liste links.');
    }
  }

  function consumeFocusListingId(): string | null {
    try {
      const raw = localStorage.getItem('otto.focus');
      if (!raw) return null;
      localStorage.removeItem('otto.focus');
      const parsed = JSON.parse(raw) as { listingId?: string; ts?: number };
      if (!parsed?.listingId || typeof parsed.listingId !== 'string') return null;
      if (typeof parsed.ts === 'number' && Date.now() - parsed.ts > 30_000) return null;
      return parsed.listingId;
    } catch {
      return null;
    }
  }

  async function resolveListingByVarioListingId(varioListingId: string): Promise<string | null> {
    const { data, error } = await ctx.supabase
      .schema('otto')
      .from('listings')
      .select('id')
      .eq('vario_listing_id', varioListingId)
      .maybeSingle();
    if (error) {
      console.warn('[otto-list] focus-resolve failed:', error.message);
      return null;
    }
    return (data as { id?: string } | null)?.id ?? null;
  }

  function updatePager(total: number) {
    const info = host.querySelector<HTMLElement>('#klst-pg-info')!;
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    info.textContent = `${total === 0 ? 0 : activePage * PAGE_SIZE + 1}–${Math.min(total, (activePage + 1) * PAGE_SIZE)} von ${total} · Seite ${activePage + 1} / ${pages}`;
    pgFirst.disabled = activePage === 0;
    pgPrev.disabled = activePage === 0;
    pgNext.disabled = activePage + 1 >= pages;
    pgLast.disabled = activePage + 1 >= pages;
  }

  function clearDetail(msg: string) {
    const detailHost = host.querySelector<HTMLDivElement>('#klst-detail')!;
    detailHost.innerHTML = `<div class="li-loading">${escapeHtml(msg)}</div>`;
    // Keine Auswahl → Slot-Titelleisten-Aktionen ausblenden.
    currentSave = null;
    if (slotSaveBtn) slotSaveBtn.hidden = true;
    if (slotSendBtn) slotSendBtn.hidden = true;
  }
}

// ---------------------------------------------------------------------------
// Rendering Helpers
// ---------------------------------------------------------------------------

function renderShell(standalone: boolean): string {
  // Titelleiste in ALLEN Pfaden identisch: „Otto verbunden" + Speichern + Senden (wie der Artikel-
  // Slot). Der Lister wird über zwei Pfade gemountet (Dashboard-Tab = standalone:false, App-Tab
  // /otto-lister via renderOttoList = standalone:true) — beide sollen dieselbe Titelleiste mit
  // Senden zeigen, daher NICHT an `standalone` gekoppelt. Die otto-slot-*-Buttons werden in
  // renderOttoListTab verdrahtet.
  const hactions =
    `<span class="otto-slot-conn"><span class="otto-slot-conn-d"></span> Otto verbunden</span>`
    + `<button type="button" id="slot-save" class="otto-slot-save" hidden><span class="v-icon fa-regular fa-floppy-disk"></span> Speichern</button>`
    + `<button type="button" id="slot-send" class="otto-slot-send" hidden><span class="v-icon fa-regular fa-paper-plane"></span> Senden</button>`
    + `<span id="slot-status"></span>`;

  const apphead = `<header class="apphead">
        <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
        <img class="otto-logo" src="/otto-market.png" alt="OTTO Market" style="height:22px;" />
        <span class="hdivider"></span>
        <div class="htitle"><div class="heyebrow">OTTO · Lister</div><h1>Listing-Übersicht</h1></div>
        <div class="hactions">${hactions}</div>
      </header>`;

  const filterbar = `<div class="filterbar">
      <div class="select"><select id="klst-status" title="Status filtern"></select></div>
      <div class="tbsearch">${KF_ICON.search}<input id="klst-search" type="search" placeholder="Listing nach Nr., Name oder Kategorie suchen…" autocomplete="off" /><button type="button" id="klst-search-clear" class="clearbtn" aria-label="Suche leeren" title="Suche leeren">${KF_ICON.close}</button></div>
    </div>`;

  const work = `<div class="work">
      <div class="card listcard">
        <div id="klst-list" class="li-scroll"></div>
        <div class="li-foot">
          <button type="button" class="pgbtn" id="klst-pg-first" title="Erste Seite">${PG_FIRST}</button>
          <button type="button" class="pgbtn" id="klst-pg-prev" title="Vorherige Seite">${PG_PREV}</button>
          <span id="klst-pg-info">–</span>
          <button type="button" class="pgbtn" id="klst-pg-next" title="Nächste Seite">${PG_NEXT}</button>
          <button type="button" class="pgbtn" id="klst-pg-last" title="Letzte Seite">${PG_LAST}</button>
        </div>
      </div>
      <div class="card detailcard"><div id="klst-detail" class="kl-detail-host"></div></div>
    </div>`;

  return standalone
    ? `<div class="kf-page" style="height:100%;display:flex;flex-direction:column;"><main class="main">${apphead}${filterbar}${work}</main></div>`
    : `<div class="kf-page" style="display:flex;flex-direction:column;height:min(78vh,840px);"><main class="main">${apphead}${filterbar}${work}</main></div>`;
}

function renderMasterList(host: HTMLElement, rows: ListingRow[], activeId: string | null): void {
  if (rows.length === 0) {
    host.innerHTML = `
      <div class="li-empty">
        <div class="li-empty-title">Keine Listings in dieser Ansicht</div>
        <div class="li-empty-hint">Wähle in Vario am Artikel eine OTTO-Kategorie — dann erscheint das Listing hier.</div>
      </div>`;
    return;
  }
  host.innerHTML = rows.map((r) => renderMasterRow(r, r.id === activeId)).join('');
}

function renderMasterRow(r: ListingRow, active: boolean): string {
  const stCls = STATUS_CLS[r.status] ?? 'st-pending';
  const stLabel = (STATUS_PILL[r.status] ?? STATUS_PILL.pending).label;
  const number = escapeHtml(r.vario_article_number ?? r.vario_article_id);
  const name = escapeHtml(r.vario_article_name ?? '—');
  const cat = escapeHtml(r.otto_category ?? '—');
  const vpPill = r.is_variant_parent ? `<span class="pill vp-pill">Variantenvater</span>` : '';
  const orderPill = r.from_order
    ? `<span class="pill order-pill" title="Über eine Bestellung zugeordnet — noch nicht auf OTTO gelistet">aus Bestellung</span>`
    : '';
  const reqPill = renderRequiredAttrsPill(r.required_attrs_total, r.required_attrs_missing);
  const errLine = (r.status === 'error' && r.last_error)
    ? `<div class="li-err">${WARN_ICON}<span>${escapeHtml(truncate(r.last_error, 140))}</span></div>`
    : '';
  const searchHaystack = [
    r.vario_article_number ?? '',
    r.vario_article_id ?? '',
    r.vario_article_name ?? '',
    r.otto_category ?? '',
  ].join(' ').toLowerCase();
  return `
    <button type="button" class="li ${active ? 'sel' : ''}" data-listing-id="${r.id}" data-search-label="${escapeHtml(searchHaystack)}">
      <div class="li-name">${name}</div>
      <div class="li-top">
        <span class="pill ${stCls}"><span class="d"></span>${escapeHtml(stLabel)}</span>
        ${vpPill}
        ${orderPill}
        ${reqPill}
      </div>
      <div class="li-sku">${number}</div>
      ${renderRowMiniProgress(r.status)}
      <div class="li-meta">
        <span class="li-cat">${TAG_ICON}${cat}</span>
      </div>
      ${errLine}
    </button>
  `;
}

function applyClientSearchFilter(host: HTMLElement, term: string): void {
  const tokens = term.trim().toLowerCase().split(/\s+/).filter((t) => t.length > 0);
  host.querySelectorAll<HTMLElement>('.li').forEach((row) => {
    if (tokens.length === 0) { row.hidden = false; return; }
    const hay = row.dataset['searchLabel'] ?? '';
    row.hidden = !tokens.every((t) => hay.includes(t));
  });
}

function renderRowMiniProgress(status: ListingStatus): string {
  let pct = 0;
  let label = 'neu';
  switch (status) {
    case 'mapped':
      pct = 100; label = 'bereit'; break;
    case 'mapping':
    case 'error':
      pct = 50; label = 'in Arbeit'; break;
    default:
      pct = 0; label = 'neu'; break;
  }
  return `
    <div class="li-prog" title="Status: ${status}">
      <div class="li-bar"><i style="width:${pct}%;"></i></div>
      <span>${label}</span>
    </div>
  `;
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
